import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import policy JSON files
import kidsPlayPolicy from '../data/policies/kids-play.json';
import quizMasterPolicy from '../data/policies/quiz-master.json';
import mathSprintArenaPolicy from '../data/policies/math-sprint-arena.json';
import brainGamesPolicy from '../data/policies/brain-games.json';

const PrivacyPolicy = ({ appId }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [rawHtml, setRawHtml] = useState('');

  const toSlug = (str) =>
    (str || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const policies = {
    'kids-play': kidsPlayPolicy,
    'quiz-master': quizMasterPolicy,
    'math-sprint-arena': mathSprintArenaPolicy,
    'brain-games': brainGamesPolicy
  };

  const selectedKey = useMemo(() => {
    if (appId) return appId;
    if (params && params.appId) return toSlug(params.appId);
    return 'task-manager-pro';
  }, [appId, params]);

  const policy = policies[selectedKey] || policies['task-manager-pro'];

  useEffect(() => {
    if (policy && policy.type === 'html' && policy.htmlPath) {
      setRawHtml('');
      fetch(policy.htmlPath)
        .then((res) => res.text())
        .then((html) => setRawHtml(html))
        .catch(() => setRawHtml('<p>Unable to load policy at this time.</p>'));
    }
  }, [selectedKey]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimalist Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-70 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Appgola.com</h1>
            </button>
            <button 
              onClick={() => navigate('/')}
              className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Title Section */}
        <div className="bg-white rounded-3xl p-10 sm:p-12 border-2 border-gray-100 mb-8 shadow-sm">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 tracking-tight">
            Privacy Policy
          </h1>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
            {policy.title}
          </h2>
          <p className="text-sm font-medium text-gray-500">
            Last Updated: {policy.lastUpdated}
          </p>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-3xl p-10 sm:p-12 border-2 border-gray-100 shadow-sm">
          {/* Raw HTML mode (Kids Play) */}
          {policy.type === 'html' ? (
            <section className="prose max-w-none">
              <div dangerouslySetInnerHTML={{ __html: rawHtml }} />
            </section>
          ) : (
            <>
          {/* Introduction */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Introduction
            </h3>
            <p className="text-gray-600 leading-relaxed text-lg">
              {policy.content.introduction}
            </p>
          </section>

          {/* Data Collection */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Data We Collect
            </h3>
            <ul className="space-y-3">
              {policy.content.dataCollection.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-gray-600 text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Data Usage */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              How We Use Your Data
            </h3>
            <ul className="space-y-3">
              {policy.content.dataUsage.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-gray-600 text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Data Security
            </h3>
            <p className="text-gray-600 leading-relaxed text-lg">
              {policy.content.dataSecurity}
            </p>
          </section>

          {/* Third-Party Sharing */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Third-Party Sharing
            </h3>
            <p className="text-gray-600 leading-relaxed text-lg">
              {policy.content.thirdParty}
            </p>
          </section>

          {/* User Rights */}
          <section className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Your Rights
            </h3>
            <p className="text-gray-600 leading-relaxed text-lg">
              {policy.content.userRights}
            </p>
          </section>

          {/* Children - Quiz Master only */}
          {policy.content.children && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                Children's Privacy
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.children}
              </p>
            </section>
          )}

          {/* Ads Info - Quiz Master only */}
          {policy.content.adsInfo && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                Ads and Advertising
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.adsInfo}
              </p>
            </section>
          )}

          {/* Optional Sign-In - Quiz Master only */}
          {policy.content.optionalSignIn && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                Sign-In Options
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.optionalSignIn}
              </p>
            </section>
          )}

          {/* Data Retention - Quiz Master only */}
          {policy.content.dataRetention && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                Data Retention
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.dataRetention}
              </p>
            </section>
          )}

          {/* International Transfers - Quiz Master only */}
          {policy.content.internationalTransfers && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                International Transfers
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.internationalTransfers}
              </p>
            </section>
          )}

          {/* Changes to Policy - Quiz Master only */}
          {policy.content.policyChanges && (
            <section className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
                Changes to this Policy
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {policy.content.policyChanges}
              </p>
            </section>
          )}

          {/* Contact */}
          <section className="pt-8 border-t-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-1.5 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full mr-3"></span>
              Contact Us
            </h3>
            <p className="text-gray-600 leading-relaxed text-lg">
              If you have any questions about this privacy policy, please contact us at{' '}
              <a 
                href={`mailto:${policy.content.contactEmail || 'digipeopleapps@gmail.com'}`}
                className="font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:from-indigo-700 hover:to-purple-700 transition-all"
              >
                {policy.content.contactEmail || 'digipeopleapps@gmail.com'}
              </a>
            </p>
          </section>
            </>
          )}
        </div>

        {/* Back Button */}
        <div className="mt-10 text-center">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Apps
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-sm font-medium text-gray-600">
              © {new Date().getFullYear()} Appgola.com. All rights reserved.
            </p>
            <a 
              href="mailto:digipeopleapps@gmail.com"
              className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors"
            >
              digipeopleapps@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
